import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Chip, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import moment from 'moment/moment';
import deepmerge from 'deepmerge';

import { MISC_DATE_ISO_FORMAT, MISC_DATE_POLISH_FORMAT } from '../../../../../../core/constants';
import PaginationControls from '../../../../../common/pagination_controls';
import useNotification from '../../../../../../core/hooks/use_notification';
import { getCustomerBlockadeHistoryRegistriesNotificationData } from '../../../../../../core/utils/notification_utils';
import { getDefaultPaginationQueryValues } from '../../../../../../core/utils/api_utils';
import { getCustomerBlockadeHistoryRegistriesAction, getCustomerBlockadeHistoryRegistriesState } from '../../../../../../features/customer/get_customers_blockade_history_registries';
import { getCustomerBlockadeRegistrySource } from '../../../../../common/utils/customer_utils';
import { CustomerBlockadeRegistrySourceEnum } from '../../../../../../core/enums/customer/customer';

const initialQueryParams = {
    ...getDefaultPaginationQueryValues,
    sort: ['createdDate', 'desc']
};

const CustomerBlockadeHistoryRegistryList = ({ customerId }) => {
    const dispatch = useDispatch();
    const { showErrorMessage } = useNotification();

    const [queryParams, setQueryParams] = useState({ ...initialQueryParams });

    const getCustomerBlockadeHistoryRegistries = useSelector(getCustomerBlockadeHistoryRegistriesState);

    useEffect(() => {
        dispatch(getCustomerBlockadeHistoryRegistriesAction({ queryParams, customerId }))
            .then(response => showErrorMessage(getCustomerBlockadeHistoryRegistriesNotificationData(response)));
    }, [customerId, dispatch, queryParams, showErrorMessage]);

    return (
        <Box>
            { getCustomerBlockadeHistoryRegistries?.loading && (
                <Box mb={ 2 }>
                    <LinearProgress/>
                </Box>
            ) }

            { getCustomerBlockadeHistoryRegistries?.data?.content && (
                <Box>
                    <TableContainer component={ Paper } variant="outlined">
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeighlt: 600 } } }>
                                    <TableCell>Data utworzenia</TableCell>
                                    <TableCell align="center">Typ blokady</TableCell>
                                    <TableCell align="center">Powód</TableCell>
                                    <TableCell align="center">Data zniesienia blokady</TableCell>
                                    <TableCell align="center">Powód zniesienia blokady</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                { getCustomerBlockadeHistoryRegistries?.data?.content?.map(registry => (
                                    <TableRow key={ registry?.id } hover>
                                        <TableCell>
                                            { moment(registry?.createdDate, MISC_DATE_ISO_FORMAT).format(MISC_DATE_POLISH_FORMAT) }
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={ getCustomerBlockadeRegistrySource(registry?.source) }
                                                color={ registry?.source === CustomerBlockadeRegistrySourceEnum.MANUAL.value ? 'primary' : 'default' }
                                                variant={ registry?.source === CustomerBlockadeRegistrySourceEnum.MANUAL.value ? 'filled' : 'outlined' }
                                                size="small"
                                                sx={ { fontWeight: 600 } }
                                            />
                                        </TableCell>
                                        <TableCell align="center" sx={{
                                            maxWidth: 200,
                                            whiteSpace: 'normal',
                                            wordBreak: 'break-word'
                                        }}>
                                            { registry?.reason }
                                        </TableCell>
                                        <TableCell align="center">
                                            { moment(registry?.liftingDate, MISC_DATE_ISO_FORMAT).format(MISC_DATE_POLISH_FORMAT) }
                                        </TableCell>
                                        <TableCell align="center" sx={{
                                            maxWidth: 200,
                                            whiteSpace: 'normal',
                                            wordBreak: 'break-word'
                                        }}>
                                            { registry?.liftingReason }
                                        </TableCell>
                                    </TableRow>
                                )) }
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box mt={ 3 } display="flex" justifyContent="center">
                        <PaginationControls
                            currentPage={ getCustomerBlockadeHistoryRegistries?.data?.pageable?.pageNumber }
                            totalPages={ getCustomerBlockadeHistoryRegistries?.data?.totalPages }
                            setPage={ page => setQueryParams(prevState => deepmerge(prevState, { page })) }
                        />
                    </Box>
                </Box>
            ) }
        </Box>
    );
};

export default CustomerBlockadeHistoryRegistryList;
